import {
  deviceInhemeterOtaInit,
  deviceInhemeterOtaStatus,
} from "api/device";
import { notifySimple } from "providers/toast";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { setLoading } from "store/appSlice";
import GenericButton from "widgets/controls/buttons/generic";
import InputField from "widgets/shared/inputs/inputField";
import GenericDialog from "./generic";

const UpdateFirmwareDialog = (props: {
  isOpen: boolean;
  onClose: () => void;
  selected: {
    snr?: string;
    meterNumber?: string;
    meternumber?: string;
    name?: string;
  };
}) => {
  const { isOpen, onClose, selected } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const meternumber = selected?.meterNumber || selected?.meternumber || selected?.snr || "";
  const [version, setVersion] = useState("v1");
  const [jobId, setJobId] = useState("");
  const [jobSummary, setJobSummary] = useState("");

  const doStart = async () => {
    if (!meternumber || !version.trim()) return;
    dispatch(setLoading(true));
    const { data, status } = await deviceInhemeterOtaInit({
      meternumber,
      version: version.trim(),
    });
    dispatch(setLoading(false));

    if (status === 200 && data?.success && data?.jobId) {
      setJobId(data.jobId);
      setJobSummary("");
      notifySimple(t("notification.update.firmware.success"), "success");
      return;
    }
    if (data?.failure) {
      notifySimple(String(data.failure), "error");
    }
  };

  const doStatus = async () => {
    if (!meternumber || !jobId) return;
    dispatch(setLoading(true));
    const { data, status } = await deviceInhemeterOtaStatus({
      meternumber,
      jobId,
    });
    dispatch(setLoading(false));

    if (status === 200 && data?.success && data?.job) {
      const job = data.job;
      const progress = `${job.currentIndex ?? 0}/${job.totalChunks ?? 0}`;
      const versionText = job.firmwareVersion ? ` · ${job.firmwareVersion}` : "";
      const errorText = job.error ? ` · ${job.error}` : "";
      setJobSummary(`${job.state} · ${progress}${versionText}${errorText}`);
      return;
    }
    if (data?.failure) {
      notifySimple(String(data.failure), "error");
    }
  };

  return (
    <GenericDialog
      isOpen={isOpen}
      title={t("dialog.update.firmware.title")}
      onExit={onClose}
      size="lg"
    >
      <div className="-mt-6 flex flex-col gap-4">
        <div className="text-txt-md font-medium text-secondary">
          {t("dialog.update.firmware.description")}
        </div>
        <InputField
          id="firmware-meter"
          value={meternumber}
          readonly
          sizes="md"
          color="secondary"
          variant="outline"
        />
        <InputField
          id="firmware-version"
          value={version}
          placeholder={t("form.input.firmware.version")}
          sizes="md"
          color="secondary"
          variant="outline"
          onChange={(val) => setVersion(String(val ?? ""))}
        />
        <div className="flex gap-2">
          <GenericButton
            onClick={doStart}
            text={t("button.action.update.firmware")}
            size="md"
            color="secondary"
            extra="flex-1"
            disabled={!meternumber || !version.trim()}
          />
          <GenericButton
            onClick={onClose}
            text={t("common.cancel")}
            size="md"
            color="secondary"
            variant="outline"
            extra="flex-1"
          />
        </div>
        {jobId && (
          <div className="flex flex-col gap-2 text-txt-sm text-secondary">
            <span>{jobId}</span>
            {jobSummary && <span>{jobSummary}</span>}
            <GenericButton
              onClick={doStatus}
              text={t("button.action.firmware.status")}
              size="md"
              color="secondary"
              variant="outline"
            />
          </div>
        )}
      </div>
    </GenericDialog>
  );
};

export default UpdateFirmwareDialog;
